import { Card } from '../types/game';

// Starter deck composition:
// 5 Movement: 5x Move, 1x Shortcut
// 3 Fieldcraft: 1x Quick Step, 1x Sprint, 1x Fade
// 4 Intel: 1x Sweep, 1x Counter-Surveillance, 1x Informant, 1x Spotter
// 4 Tactics: 1x Hide Inside, 1x Decoy, 1x Hotwire, 1x Rooftop Access
// 2 Combat: 1x Improvised Strike, 1x Instinct

export const CARD_TEMPLATES: Record<string, Omit<Card, 'id'>> = {
  move: {
    name: 'Move',
    category: 'movement',
    apCost: 1,
    description: 'Move up to 300m along streets.',
    movementRange: 300,
  },
  shortcut: {
    name: 'Shortcut',
    category: 'movement',
    apCost: 1,
    description: 'Stealth move — cut through buildings and water in a straight line. 0 signal. Max 150m.',
    movementRange: 150,
    signalModifier: 0,
    isShortcut: true,
  },
  quickStep: {
    name: 'Quick Step',
    category: 'fieldcraft',
    apCost: 1,
    description: 'If the next card played is a Move card, signal reduced by 50%.',
    isFieldcraft: true,
    buffSignalModifier: 0.5,
  },
  sprint: {
    name: 'Sprint',
    category: 'fieldcraft',
    apCost: 1,
    description: 'If the next card played is a Move card, double its movement range.',
    isFieldcraft: true,
    buffRangeMultiplier: 2,
  },
  fade: {
    name: 'Fade',
    category: 'fieldcraft',
    apCost: 1,
    description: 'If the next card played is a Move card, zero signal if you end in a residential zone.',
    isFieldcraft: true,
    buffSignalModifier: 0,
    buffSignalCondition: 'residential-zone',
  },
  sweep: {
    name: 'Sweep',
    category: 'intel',
    apCost: 1,
    description: 'Place a 500m scan zone anywhere on the map. Turns red if the agent is inside, gray if not. Decays over 3 turns.',
    sweepRadius: 500,
  },
  counterSurveillance: {
    name: 'Counter-Surveillance',
    category: 'tactics',
    apCost: 0,
    description: '−100 exposure. Reveals your own passive signal from last turn AND the agent\'s passive signals from last turn.',
  },
  informant: {
    name: 'Informant',
    category: 'intel',
    apCost: 2,
    description: 'Receive a tip: a named landmark near the agent and the street they\'re on.',
  },
  spotter: {
    name: 'Spotter',
    category: 'intel',
    apCost: 2,
    description: 'Place surveillance at your location. 200m radius. Any enemy entering is revealed for 3 turns.',
  },
  bearing: {
    name: 'Bearing',
    category: 'intel',
    apCost: 1,
    description: 'Draws a line from your current position toward the agent\'s current position, extending to the map edge. Angle only, no distance. Persists 3 turns and fades with age. Multiple reads from different positions triangulate.',
  },
  reconPlane: {
    name: 'Recon Plane',
    category: 'intel',
    apCost: 1,
    description: 'Flyover sweep. Click-drag to place a 300m-wide strip across the map at any angle. Returns a single yes/no: is the agent inside the strip right now? Result visible for 3 turns.',
  },
  stakeout: {
    name: 'Stakeout',
    category: 'intel',
    apCost: 3,
    description: 'Place a comrade on any commercial building and set their vision cone direction. They watch for the rest of the round. If the agent enters the cone, you get their exact position as "ENEMY SEEN" — the 3-turn fade only starts counting once the agent leaves the cone.',
    condition: 'Commercial buildings only.',
  },
  surveillanceVan: {
    name: 'Surveillance Van',
    category: 'intel',
    apCost: 3,
    description: 'Deploy a van on a vehicle-accessible street and draw a patrol route up to 600m. The van moves 200m per turn along the route, ping-ponging at the ends. 360° / 200m vision — if the agent passes through the cone, "ENEMY SEEN" for 3 turns. Multiple vans stack.',
    condition: 'Vehicle-accessible streets only.',
  },
  trafficAnalysis: {
    name: 'Traffic Analysis',
    category: 'intel',
    apCost: 2,
    description: 'Reveals which named major or minor road the agent is currently on. Highlights the full connected road on the map for the current turn. Nothing to show if the agent is on a residential street, service road, footpath, or off-road.',
  },
  hideInside: {
    name: 'Hide Inside',
    category: 'tactics',
    apCost: 1,
    description: 'Enter a building. Zero signal this turn and next. Cannot move while hiding. Residential buildings require a Lock Pick.',
    condition: 'Must be near a building. Residential zone requires Lock Pick.',
  },
  decoy: {
    name: 'Decoy',
    category: 'tactics',
    apCost: 3,
    description: 'Place a strong fake signal anywhere on the map. Appears as a convincing blip to the agent.',
  },
  improvisedStrike: {
    name: 'Improvised Strike',
    category: 'combat',
    apCost: 1,
    description: 'Prep: +10% to ambush strikes and gun confrontations. On a visible target: attack with your Prep %.',
  },
  instinct: {
    name: 'Instinct',
    category: 'combat',
    apCost: 1,
    description: '360° vision cone for 1 turn. See everything around you. +5% Prep.',
  },
  hotwire: {
    name: 'Hotwire',
    category: 'tactics',
    apCost: 3,
    description: 'Hotwire a parked car on a residential street. Doubles movement range. Major roads double signal & exposure. Exit anytime.',
    condition: 'Must be on a residential street.',
  },
  rooftopAccess: {
    name: 'Rooftop Access',
    category: 'tactics',
    apCost: 3,
    description: 'Climb to a rooftop. 2× vision range, 120° cone. Binoculars give 4× range. Sniper rifle enables long-range ambush. Agent can\'t see you unless also elevated.',
    condition: 'Must be adjacent to a commercial building.',
  },
  wireTap: {
    name: 'Wire Tap',
    category: 'intel',
    apCost: 2,
    description: 'Place on a street segment. When the agent crosses it, their exact position and heading are revealed for 2 turns. Tap deactivates after triggering.',
  },
  districtScan: {
    name: 'District Scan',
    category: 'intel',
    apCost: 2,
    description: 'Highlights which neighborhood the agent is currently in using OSM boundaries. Broad but useful for narrowing a search.',
  },
  acousticPing: {
    name: 'Acoustic Ping',
    category: 'intel',
    apCost: 2,
    description: 'Reveals straight-line distance to the agent in meters, shown as a range ring centered on you. No direction — multiple pings from different positions enable triangulation.',
  },
  traceRoute: {
    name: 'Trace Route',
    category: 'intel',
    apCost: 2,
    description: 'Reveals the agent\'s older movement path as a fading trail on the map. Excludes the most recent turn — shows where they\'ve been, not where they are.',
  },
  misdirect: {
    name: 'Misdirect',
    category: 'tactics',
    apCost: 2,
    description: 'Draw a fake movement path (up to 300m) on the map. Generates a real signal as if you walked there, but you don\'t actually move.',
  },
  ghost: {
    name: 'Ghost',
    category: 'tactics',
    apCost: 1,
    description: 'All signal you give off this turn is negated. Move freely without a trace until end of turn.',
  },
  taxi: {
    name: 'Taxi',
    category: 'tactics',
    apCost: 3,
    description: 'Hail a taxi to any destination on the road network. Auto-drives 600m per turn along major/minor roads. Exit any time. More signal than walking, less than a hotwired car. No footpaths.',
    condition: 'Must be on a major or minor road.',
  },
  disguise: {
    name: 'Disguise',
    category: 'tactics',
    apCost: 2,
    description: '3 turns: 75% less signal in commercial zones and on major roads, 50% less exposure build, and transit boardings read as normal movement (no ground-truth to the agent).',
  },
  malware: {
    name: 'Malware',
    category: 'tactics',
    apCost: 2,
    description: 'Inject an unplayable junk card into the agent\'s deck for the rest of the round. When drawn it occupies a hand slot and is discarded at end of turn without AP cost. Multiple copies stack.',
  },
  stageExit: {
    name: 'Stage Exit',
    category: 'tactics',
    apCost: 2,
    description: 'Broadcast a fake boarding at your current stop and a fake exit at the next stop on the line. The agent commits to the wrong stop. You stay put.',
    condition: 'Must be near a transit stop.',
  },
  doubleAgent: {
    name: 'Double Agent',
    category: 'tactics',
    apCost: 1,
    isBurn: true,
    description: 'Burn. The next Informant-type intel card the agent plays points to a random POI on the map instead of one near you. Persists until triggered or end of round.',
  },
  lowProfile: {
    name: 'Low Profile',
    category: 'tactics',
    apCost: 1,
    description: 'Immediately remove 200 exposure. No additional exposure is gained for the rest of this turn.',
  },
  paradeRoute: {
    name: 'Parade Route',
    category: 'tactics',
    apCost: 1,
    description: 'The agent runs into a parade on their route. Next turn, the agent\'s movement distance is halved.',
  },
  stopAndFrisk: {
    name: 'Stop and Frisk',
    category: 'tactics',
    apCost: 2,
    description: 'Local police stop the agent for questioning. Next turn, the agent cannot play any Move or Shortcut cards.',
  },
  overwatch: {
    name: 'Overwatch',
    category: 'combat',
    apCost: 1,
    description: 'Hold a 300m road segment centered on your position for up to 3 turns. If the agent crosses the segment, you ambush with your weapon. Can exit early. Blocks Move/Shortcut while active.',
    condition: 'Must hold a pistol or sniper rifle.',
  },
  static: {
    name: 'Static',
    category: 'tactics',
    apCost: 1,
    description: 'Your signal this turn is displaced to a random street within 200m. Cheap misdirection — sometimes helpful, sometimes harmless.',
  },
  deadMansSwitch: {
    name: 'Dead Man\'s Switch',
    category: 'tactics',
    apCost: 2,
    description: 'Captures the signal you generated this turn and replays it at this exact location 3 turns from now. Great for luring the agent back to an already-cleared area.',
  },
  doppelganger: {
    name: 'Doppelgänger',
    category: 'tactics',
    apCost: 3,
    isBurn: true,
    description: 'Burn. Draw an 800m path. A convincing double walks the path at 200m/turn (4 turns) generating real-looking signals. If the agent engages the double, it dissipates and the agent\'s exact position is revealed to you for 3 turns.',
  },

  // --- Agent junk (Malware-injected) ---
  // Not a player-draftable card. Lives only in the agent's deck after Malware
  // is played. The 'junk' category keeps it out of every player-facing draft,
  // reference, and shop pool; isJunk tells the agent AI to skip it and the
  // end-of-turn discard sweeps it back to the agent's discard pile without
  // consuming AP.
  agentJunkMalware: {
    name: 'Corrupted Intel',
    category: 'junk',
    apCost: 99, // un-affordable safety net — the AI filter handles the real skip
    description: 'Junk data — malware in the agent\'s network. Clogs the hand until discarded.',
    isJunk: true,
  },

  // --- Player junk (agent Ping passive) ---
  // Not draftable. Only lives in the PLAYER's hand, injected every 3 turns
  // by an agent that has the Ping passive. Costs 1 AP to play (pure
  // discard — no other effect). If still in hand at end of turn, the
  // player's exact position is broadcast to the agent for 2 turns via
  // the existing detectedTurnsRemaining mechanism.
  playerPingJunk: {
    name: 'Ping',
    category: 'junk',
    apCost: 1,
    description: 'Agent is pinging your signal. Pay 1 AP to suppress it or the agent gets your exact position at end of turn.',
    isJunk: true,
  },

  // --- Fieldcraft: AP Manipulation ---
  adrenaline: {
    name: 'Adrenaline',
    category: 'fieldcraft',
    apCost: 0,
    description: 'Gain +1 AP this turn.',
  },
  detour: {
    name: 'Detour',
    category: 'fieldcraft',
    apCost: 0,
    isChain: true,
    description: 'Gain +1 AP. Chain: discard 1 card from hand, then draw 1.',
  },
  calculated: {
    name: 'Calculated',
    category: 'fieldcraft',
    apCost: 1,
    description: 'Gain +2 AP at the start of your next turn. Invest now, act big later.',
  },

  // --- Fieldcraft: Card Manipulation ---
  adapt: {
    name: 'Adapt',
    category: 'fieldcraft',
    apCost: 1,
    description: 'Discard 1 card from your hand, then draw 3. Dig deep for what you need.',
  },
  copy: {
    name: 'Copy',
    category: 'fieldcraft',
    apCost: 2,
    description: 'Burn. Choose a card in your hand. A duplicate is added to your discard pile — you\'ll draw it again later this round.',
    isBurn: true,
  },
  recall: {
    name: 'Recall',
    category: 'fieldcraft',
    apCost: 1,
    description: 'Chain. Search your discard pile and add 1 card to your hand. This card is discarded after use.',
    isChain: true,
  },
  stash: {
    name: 'Stash',
    category: 'fieldcraft',
    apCost: 0,
    description: 'Choose a card in your hand. It stays in your hand at end of turn instead of being discarded.',
  },

  // --- Fieldcraft: Card Upgrading ---
  fieldModify: {
    name: 'Field Modify',
    category: 'fieldcraft',
    apCost: 1,
    isBurn: true,
    description: 'Burn. Choose a card in your hand — it costs 1 less AP for the rest of the round (min 0).',
  },
  overclock: {
    name: 'Overclock',
    category: 'fieldcraft',
    apCost: 1,
    isBurn: true,
    description: 'Burn. Choose a movement card in your hand — its range is doubled for the rest of the round.',
  },
  sharpen: {
    name: 'Sharpen',
    category: 'fieldcraft',
    apCost: 1,
    isBurn: true,
    description: 'Burn. For the rest of this turn, gain +10% Prep for every card you play (including this one).',
  },

  // --- Fieldcraft: Conditional / Synergy ---
  patience: {
    name: 'Patience',
    category: 'fieldcraft',
    apCost: 0,
    isBurn: true,
    description: 'Burn. Play only if you play no other card this turn. Gain +1 AP next turn for the rest of the round.',
  },
  opportunist: {
    name: 'Opportunist',
    category: 'fieldcraft',
    apCost: 0,
    description: 'Can only be played immediately after a movement card. Draw 2 cards.',
    condition: 'Must play immediately after a movement card.',
  },
  operationalIntegrity: {
    name: 'Operational Integrity',
    category: 'fieldcraft',
    apCost: 0,
    description: 'If this is the only card played this turn, next turn start with +2 AP and draw 3 additional cards.',
    condition: 'Must be the only card played this turn.',
  },

  // --- Fieldcraft: Deck Manipulation ---
  improvise: {
    name: 'Improvise',
    category: 'fieldcraft',
    apCost: 0,
    description: 'Look at the top 3 cards of your deck. Choose 1 to add to your hand, put the rest back.',
  },

  // --- Fieldcraft: Combo Enablers ---
  doubleTap: {
    name: 'Double Tap',
    category: 'fieldcraft',
    apCost: 1,
    description: 'Choose a card in your hand. A 0-AP duplicate is added. The duplicate burns at end of turn if not played.',
  },
  contingency: {
    name: 'Contingency',
    category: 'fieldcraft',
    apCost: 1,
    description: 'The next Tactics card you draw costs 0 AP. Lasts until a Tactics card is drawn.',
  },
};

export function createCard(templateKey: string): Card {
  const template = CARD_TEMPLATES[templateKey];
  if (!template) throw new Error(`Unknown card template: ${templateKey}`);
  return { ...template, id: `card-${Math.random().toString(36).slice(2, 10)}` };
}

// Card template keys eligible for shop drafts (everything except basic Move
// and any junk-category templates like the Malware payload). Filtering by
// category here means future junk cards are auto-excluded without needing
// to update this allow-list.
export const DRAFTABLE_CARD_KEYS = Object.keys(CARD_TEMPLATES).filter(
  k => k !== 'move' && CARD_TEMPLATES[k].category !== 'junk',
);

export function createStarterDeck(): Card[] {
  return [
    // 5 Move cards
    createCard('move'),
    createCard('move'),
    createCard('move'),
    createCard('move'),
    createCard('move'),
    // 1 Shortcut
    createCard('shortcut'),
    // 3 Fieldcraft
    createCard('quickStep'),
    createCard('sprint'),
    createCard('fade'),
    // 4 Intel
    createCard('sweep'),
    createCard('counterSurveillance'),
    createCard('informant'),
    createCard('spotter'),
    // 4 Tactics
    createCard('hideInside'),
    createCard('decoy'),
    createCard('hotwire'),
    createCard('rooftopAccess'),
    createCard('ghost'),
    // 2 Combat
    createCard('improvisedStrike'),
    createCard('instinct'),
    // Fieldcraft (for testing — 1 of each)
    createCard('adrenaline'),
    createCard('detour'),
    createCard('calculated'),
    createCard('adapt'),
    createCard('copy'),
    createCard('recall'),
    createCard('stash'),
    createCard('fieldModify'),
    createCard('overclock'),
    createCard('sharpen'),
    createCard('patience'),
    createCard('opportunist'),
    createCard('operationalIntegrity'),
    createCard('improvise'),
    createCard('doubleTap'),
    createCard('contingency'),
    // New intel + tactics
    createCard('wireTap'),
    createCard('districtScan'),
    createCard('acousticPing'),
    createCard('traceRoute'),
    createCard('misdirect'),
    createCard('taxi'),
    createCard('disguise'),
    createCard('malware'),
    createCard('stageExit'),
    createCard('doubleAgent'),
    createCard('bearing'),
    createCard('reconPlane'),
    createCard('stakeout'),
    createCard('surveillanceVan'),
    createCard('trafficAnalysis'),
  ];
}

export function shuffleDeck(deck: Card[]): Card[] {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function drawHand(deck: Card[], count: number): { hand: Card[]; remaining: Card[] } {
  const hand = deck.slice(0, count);
  const remaining = deck.slice(count);
  return { hand, remaining };
}

// --- Agent Deck Templates ---

/**
 * Agent archetype → deck template. Unified with types/game.ts's
 * AgentArchetype so the loadout generator can hand an archetype straight
 * through without remapping. 'sniper' exists in the type for future use
 * but isn't currently rolled by generateAgentLoadout — it unlocks once
 * sniper AI behavior (overwatch positioning, long-range combat) is
 * implemented in a later phase. Until then, leaving the template here
 * is harmless dead data.
 *
 * Each deck is 20-22 cards, giving 4-5 hand draws per round over a
 * typical round length. Card mix is tuned to let scoreCard() express
 * archetype personality via the card it picks first each turn — tracker
 * leans intel+chase, commander leans aggression, ghost leans stealth,
 * sniper leans positioning.
 */
import type { AgentArchetype } from '../types/game';
export type AgentDeckType = AgentArchetype;

export const AGENT_DECK_TEMPLATES: Record<AgentDeckType, string[]> = {
  tracker: [
    'move', 'move', 'move', 'move', 'move',
    'shortcut',
    'sprint',
    'quickStep',
    'improvisedStrike', 'improvisedStrike',
    'sweep', 'sweep',
    'counterSurveillance',
    'instinct',
    'spotter',
    'informant', 'informant',
    'sharpen',
    'fade',
  ],
  commander: [
    'move', 'move', 'move', 'move',
    'shortcut',
    'improvisedStrike', 'improvisedStrike', 'improvisedStrike',
    'sharpen', 'sharpen',
    'sweep', 'sweep',
    'counterSurveillance',
    'instinct',
    'sprint',
    'spotter',
    'informant',
    'rooftopAccess',
  ],
  ghost: [
    'move', 'move', 'move',
    'shortcut', 'shortcut',
    'quickStep', 'quickStep',
    'fade', 'fade',
    'improvisedStrike', 'improvisedStrike',
    'sweep', 'sweep',
    'counterSurveillance',
    'instinct',
    'spotter',
    'informant',
    'sharpen',
  ],
  sniper: [
    'move', 'move', 'move',
    'shortcut',
    'sprint',
    'improvisedStrike', 'improvisedStrike',
    'rooftopAccess', 'rooftopAccess',
    'sweep', 'sweep',
    'counterSurveillance',
    'instinct',
    'spotter', 'spotter',
    'informant',
    'sharpen',
  ],
};

export function createAgentDeck(archetype: AgentDeckType): Card[] {
  const template = AGENT_DECK_TEMPLATES[archetype] ?? AGENT_DECK_TEMPLATES.tracker;
  return template.map(key => createCard(key));
}
